import React, { FC, useEffect, useRef, useState } from "react";
import { Stack, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import {
  Callout,
  Headline,
  Image,
  Paragraph,
  PlainTable,
} from "@components/Content";
import {
  CalloutProps,
  DownloadCTAProps,
  HeadlineProps,
  ImageProps,
  ParagraphProps,
  PlainTableProps,
} from "@components/Content/types";
import { Colors } from "@theme/colors";
import DownloadCTA from "@src/components/CTAs/download";
import ShareButtons from "@src/components/ShareButtons";
import { useSbdArticlesDataById } from "@src/hooks/sbdArticlesById";
import { PageContextMeta } from "@src/templates/base";

type HeaderLink = {
  el: HTMLElement | null;
  index: number;
};

type ArticleSection =
  | HeadlineProps
  | ParagraphProps
  | DownloadCTAProps
  | CalloutProps
  | ImageProps
  | PlainTableProps;

interface Props {
  sections: Array<ArticleSection>;
  pageContextMeta?: PageContextMeta;
}

const Article: FC<Props> = ({ sections, pageContextMeta = {} }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("lg"));
  const sidebar = useRef<HTMLElement | null>(null);
  const container = useRef<HTMLElement | null>(null);
  const bottom = useRef<number | undefined>(undefined);
  const titleRefs = useRef<Array<HeaderLink>>([]);
  const [activeLink, setActiveLink] = useState<number>(-1);

  const [articleData, setArticleData] = useState<any>();

  const articlesData = useSbdArticlesDataById();

  useEffect(() => {
    sidebar.current = document.getElementById("sidebar");
    container.current = document.getElementById("container");
    bottom.current = sidebar.current?.getBoundingClientRect().bottom;

    const refs: Array<HeaderLink> = [];

    sections.forEach((section: ArticleSection, idx) => {
      if (section.type === "headline") {
        const target = section.slug ? section.slug : `article-section-${idx}`;
        const item: HeaderLink = {
          el: document.getElementById(target),
          index: idx,
        };
        refs.push(item);
      }
    });
    titleRefs.current = refs;

    window.addEventListener("scroll", positionNav);
    return () => {
      window.removeEventListener("scroll", positionNav);
    };
  }, [isSmall]);

  useEffect(() => {
    if (window.location.hash) {
      const hash = window.location.hash.substring(1);
      setTimeout(() => {
        const target = document.getElementById(hash);
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }

    if (pageContextMeta) {
      setArticleData(articlesData[pageContextMeta?.path ?? ""] ?? {});
    }
  }, []);

  const positionNav = () => {
    if (sidebar.current && container.current && bottom.current !== undefined) {
      const bc = container.current.getBoundingClientRect().bottom;
      const diff =
        bc < sidebar.current.offsetHeight + 160
          ? sidebar.current.offsetHeight + 160 - bc
          : 0;
      const offset = window.scrollY < 360 ? window.scrollY : 360 + diff;

      requestAnimationFrame(() => {
        // TS requires this check
        if (sidebar.current) {
          sidebar.current.style.top = `${460 - offset}px`;
        }
      });
    }

    let match = false;

    titleRefs.current.forEach((item) => {
      if (
        item.el &&
        item.el.offsetTop + 150 < window.scrollY &&
        item.el.offsetTop + 250 < window.scrollY
      ) {
        requestAnimationFrame(() => {
          setActiveLink(item.index);
        });

        match = true;
      }
    });

    if (!match) {
      requestAnimationFrame(() => {
        setActiveLink(-1);
      });
    }
  };

  const onClick = (target: string) => {
    const el = document.getElementById(target);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Stack direction="row" spacing={3} sx={{ margin: "0 auto" }}>
      {!isSmall && (
        <Stack
          id="container"
          sx={{
            width: "308px",
          }}
        >
          <Stack
            id="sidebar"
            spacing={3}
            sx={{
              position: "fixed",
              width: "308px",
            }}
          >
            <ShareButtons
              pathToShare={articleData?.path ?? ""}
              titleToShare={articleData?.title ?? ""}
              blurbToShare={articleData?.blurb ?? ""}
              utmCampaign="sbd"
            />

            <Typography
              variant="body2"
              sx={{
                fontSize: "18px",
                lineHeight: "24px",
                fontWeight: "400",
                color: Colors.TextPrimary,
              }}
            >
              Contents
            </Typography>
            <>
              {sections.map((section: ArticleSection, idx) => {
                if (
                  section.type === "headline" &&
                  (section.variant === undefined || section.variant === "h2")
                ) {
                  const target = section.slug
                    ? section.slug
                    : `article-section-${idx}`;
                  return (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "16px",
                        lineHeight: "24px",
                        fontWeight: "300",
                        cursor: "pointer",
                        color:
                          idx === activeLink
                            ? Colors.InfoMain
                            : Colors.TextPrimary,
                      }}
                      onClick={() => onClick(target)}
                      key={`article-link-${idx}`}
                    >
                      {section.content}
                    </Typography>
                  );
                }
              })}

              {/* Specifically for the new cta sidebar elements, we want them at the end */}
              {sections.map((section: ArticleSection, idx) => {
                if (section.type === "download-cta") {
                  return (
                    <DownloadCTA
                      header={section.header}
                      subHeader={section.subHeader}
                      ctaLabel={section.ctaLabel}
                      ctaHref={section.ctaHref}
                      downloadThumbnail={section.downloadThumbnail}
                      place="sidebar"
                    />
                  );
                }
              })}
            </>
          </Stack>
        </Stack>
      )}
      <Stack
        component="article"
        sx={{
          maxWidth: "748px",
          padding: { xs: "0 24px", md: "0" },
        }}
      >
        {sections.map((section: ArticleSection, idx) => {
          const sectionKey = `article-section-${idx}`;

          if (section.type === "paragraph") {
            return <Paragraph content={section.content} key={sectionKey} />;
          } else if (section.type === "headline") {
            const link = section.slug ? section.slug : sectionKey;
            return (
              <Headline
                content={section.content}
                variant={section.variant}
                link={link}
                key={sectionKey}
                topAnchorScrollPadding={true}
              />
            );
          } else if (section.type === "callout") {
            return <Callout content={section.content} key={sectionKey} />;
          } else if (section.type === "download-cta") {
            return (
              <DownloadCTA
                header={section.header}
                subHeader={section.subHeader}
                ctaLabel={section.ctaLabel}
                ctaHref={section.ctaHref}
                downloadThumbnail={section.downloadThumbnail}
              />
            );
          } else if (section.type === "image") {
            return (
              <Image
                url={section.url}
                width={section.width}
                height={section.height}
                caption={section.caption}
                key={sectionKey}
              />
            );
          } else if (section.type === "table") {
            return <PlainTable content={section.content} key={sectionKey} />;
          }
        })}
      </Stack>
    </Stack>
  );
};

export default Article;
